import { Box, Title } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { DataGrid, Performance, PlaceholderValue } from "@/components/elements";
import { LabelContentLocker } from "@/components/widgets/ContentLocker/LabelContentLocker";
import { getLimitedLabels } from "@/config/plans.config";
import { defaultCurrency } from "@/lib/currency";
import { formatCurrency, formatNumber } from "@/utils/formatEuro";

import type { LabelValueProps } from "@/components/elements";
import type { Currency } from "@/types/currency";
import type { Garage } from "@/types/garage";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type GarageMetricsViewProps = {
  data?: Garage["data"];
  loading?: boolean;
} & MantineStyleProps;

export const GarageMetricsView: FC<GarageMetricsViewProps> = ({ data, ...props }) => {
  const { t } = useTranslation();
  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const values: LabelValueProps[] = data
    ? [
        {
          label: t("garage.purchase_price"),
          value:
            data.acquisition_cost !== undefined && data.acquisition_cost !== 0 ? (
              formatCurrency(data.acquisition_cost, currency.code)
            ) : (
              <Box mb="xs">
                <PlaceholderValue w={100} lines={1} />
              </Box>
            ),
        },
        {
          label: t("garage.max_drawdown"),
          value:
            data.max_drawdown !== undefined && data.max_drawdown !== 0 ? (
              !getLimitedLabels.includes(t("garage.max_drawdown")) ? (
                formatNumber(data.max_drawdown, { style: "percent", maximumFractionDigits: 1 })
              ) : (
                <LabelContentLocker />
              )
            ) : (
              <Box mb="xs">
                <PlaceholderValue w={130} lines={1} />
              </Box>
            ),
          infoIcon:
            data.max_drawdown !== undefined && data.max_drawdown !== 0 && t("tooltips.maxDrawdown"),
        },
        {
          label: t("garage.time_to_recovery"),
          value:
            data.time_to_recovery !== undefined && data.time_to_recovery !== 0 ? (
              t("garage.time_to_recovery_value", { count: data.time_to_recovery })
            ) : (
              <Box mb="xs">
                <PlaceholderValue w={100} lines={1} />
              </Box>
            ),
          infoIcon:
            data.max_drawdown !== undefined &&
            data.max_drawdown !== 0 &&
            t("tooltips.timeToRecovery"),
        },
        {
          label: t("garage.sharpe_ratio"),
          value:
            data.sharpe_ratio !== undefined && data.sharpe_ratio !== 0 ? (
              getLimitedLabels.includes(t("garage.sharpe_ratio")) ? (
                formatNumber(data.sharpe_ratio, { maximumFractionDigits: 3 })
              ) : (
                <LabelContentLocker />
              )
            ) : (
              <Box mb="xs">
                <PlaceholderValue w={110} lines={1} />
              </Box>
            ),
          infoIcon:
            data.max_drawdown !== undefined && data.max_drawdown !== 0 && t("tooltips.sharpeRatio"),
        },
        {
          label: t("garage.best_performing_car_1y"),
          value: data.best_performing_car?.name ? (
            <Performance
              description={data.best_performing_car.name}
              value={data.best_performing_car.value}
            />
          ) : (
            <Box mb="xs">
              <PlaceholderValue w={130} lines={1} />
            </Box>
          ),
        },
        {
          label: t("garage.worst_performing_car_1y"),
          value: data.worst_performing_car?.name ? (
            <Performance
              description={data.worst_performing_car.name}
              value={data.worst_performing_car.value}
            />
          ) : (
            <Box mb="xs">
              <PlaceholderValue w={150} lines={1} />
            </Box>
          ),
        },
      ]
    : [];

  return (
    <DataGrid
      cols={{ base: 2, sm: 4 }}
      items={values}
      title={<Title order={3}>{t("garage.metrics")}</Title>}
      {...props}
    />
  );
};
